import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { Globe, TrendingUp, Clock, BookOpen, Trophy, Sparkles, Users, Flame, ListOrdered, LayoutGrid, Award, Megaphone, Map, ThumbsUp, MessageSquare } from 'lucide-react';
import SanctuaryFavorites from '../components/SanctuaryFavorites';
import UserProfileModal from '../components/UserProfileModal';
import CommunityModal from '../components/CommunityModal';
import { WOOD_THEMES, DEFAULT_WOOD_THEME_KEY } from '../themes/woodThemes';

// Affiliate link helper, matching the pattern used in BookDetailModal/Dashboard:
// search by ISBN when we have one, otherwise fall back to a title+author search.
const BOOKSHOP_AFFILIATE_ID = "125712";
const getBookshopLink = (book) => {
  const query = encodeURIComponent(
    book.isbn ? book.isbn : `${book.title} ${book.author || ""}`.trim()
  );
  return `https://bookshop.org/search?keywords=${query}&affiliate=${BOOKSHOP_AFFILIATE_ID}`;
};

// A horizontally-scrolling wood-shelf of book covers, shared by every
// featured category on this page (bestsellers, trending, garden, genres).
// Empty shelves aren't rendered at all -- see ShelfSection.
const BookShelf = ({ books }) => {
  return (
    <div style={featuredShelfWood}>
      {books.map((book, i) => (
        <a
          key={`${book.title}-${i}`}
          href={getBookshopLink(book)}
          target="_blank"
          rel="noopener noreferrer"
          style={featuredBookLink}
          title={`Buy "${book.title}" on Bookshop.org`}
        >
          {book.cover ? (
            <img src={book.cover} alt={book.title} style={featuredBookCover} />
          ) : (
            <div style={{ ...featuredBookCover, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.7rem', textAlign: 'center', padding: '4px' }}>
              {book.title}
            </div>
          )}
          <div style={featuredBookLabel}>{book.title}</div>
          {book.genre && <div style={featuredBookSubLabel}>{book.genre}</div>}
        </a>
      ))}
    </div>
  );
};

const ShelfSection = ({ icon, title, subtext, books }) => {
  if (!books || books.length === 0) return null;
  return (
    <div style={sectionContainer}>
      <div style={sectionHeader}>
        {icon}
        <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-main)' }}>{title}</h3>
      </div>
      <p style={shelfSubtext}>{subtext}</p>
      <BookShelf books={books} />
    </div>
  );
};

const HUB_LINKS = [
  { tab: 'updates', label: "What's New", Icon: Megaphone },
  { tab: 'roadmap', label: 'Roadmap', Icon: Map },
  { tab: 'voting', label: 'Vote on Features', Icon: ThumbsUp },
  { tab: 'feedback', label: 'Send Feedback', Icon: MessageSquare },
];

const Community = () => {
  const { user } = useAuth();
  const [globalStats, setGlobalStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [bestsellers, setBestsellers] = useState([]);
  const [trending, setTrending] = useState([]);
  const [topGarden, setTopGarden] = useState([]);
  const [topGenres, setTopGenres] = useState([]);
  const [readers, setReaders] = useState([]);
  const [awards, setAwards] = useState([]);
  const [peekUserId, setPeekUserId] = useState(null);
  const [hubTab, setHubTab] = useState(null);

  useEffect(() => {
    fetchGlobalStats();
    fetchBestsellers();
    fetchFeaturedShelf();
    fetchReaders();
    fetchAwards();
  }, []);

  // Book awards (Hidden Gem, New Release, ...) from the nightly trophy cron,
  // shown as covers on a shelf. One entry per title -- different editions of
  // the same book can each earn a row.
  const fetchAwards = async () => {
    const { data, error } = await supabase
      .from('book_trophies')
      .select('isbn, title, author, cover_url, trophy_label')
      .not('cover_url', 'is', null)
      .order('score', { ascending: false });
    if (error) return;
    const seen = new Set();
    setAwards((data || []).filter(t => {
      const key = t.title.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map(t => ({ title: t.title, author: t.author, isbn: t.isbn, cover: t.cover_url, genre: t.trophy_label })));
  };

  const fetchGlobalStats = async () => {
    try {
      const { data, error } = await supabase.rpc('get_community_stats');
      if (error) throw error;
      setGlobalStats(data);
    } catch (err) {
      console.error("Failed to fetch community stats:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  // NYT bestsellers, pulled from the shared book_cache table.
  const fetchBestsellers = async () => {
    try {
      const { data, error } = await supabase
        .from('book_cache')
        .select('title, author, isbn, cover')
        .not('cover', 'is', null)
        .eq('is_bestseller', true)
        .order('bestseller_rank', { ascending: true })
        .limit(8);
      if (error) throw error;
      if (data && data.length > 0) {
        setBestsellers(data);
        return;
      }
      // Fallback for the (rare) case the nightly NYT seeder hasn't populated
      // any bestsellers yet -- keep the shelf non-empty rather than blank.
      const { data: fallback, error: fallbackErr } = await supabase
        .from('book_cache')
        .select('title, author, isbn, cover')
        .not('cover', 'is', null)
        .order('created_at', { ascending: false })
        .limit(8);
      if (fallbackErr) throw fallbackErr;
      setBestsellers(fallback || []);
    } catch (err) {
      console.error("Failed to fetch bestsellers:", err);
    }
  };

  // Trending / Top of the Garden / Top by Genre -- all computed server-side
  // from real member activity (see migration 42), fully automatic.
  const fetchFeaturedShelf = async () => {
    try {
      const { data, error } = await supabase.rpc('get_featured_shelf');
      if (error) throw error;
      setTrending(data?.trending || []);
      setTopGarden(data?.top_garden || []);
      setTopGenres(data?.top_genres || []);
    } catch (err) {
      console.error("Failed to fetch featured shelf:", err);
    }
  };

  const fetchReaders = async () => {
    const { data } = await supabase
      .from("profiles")
      .select("id, username, avatar_url")
      .eq("nook_public", true)
      .limit(20);
    setReaders(data || []);
  };

  return (
    <div className="fade-in">
      {!user && (
        <div style={loggedOutBannerStyle}>
          <Globe size={18} color="var(--accent)" style={{ flexShrink: 0 }} />
          <span>
            You're browsing as a guest — <strong>sign in</strong> to see your own shelf and track your reading.
            Here's what the community's reading in the meantime.
          </span>
        </div>
      )}

      <div style={sectionContainer}>
        <div style={sectionHeader}>
          <Globe size={20} color="var(--accent)" />
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-main)' }}>Global Pulse</h3>
        </div>

        {statsLoading ? (
          <p style={{ fontSize: '0.9rem', color: 'var(--text-soft)' }}>Gathering pulse...</p>
        ) : globalStats && (
          <div style={communityGrid}>
            <div style={communityStatItem}>
              <TrendingUp size={16} color="#4caf50" />
              <div>
                <div style={communityVal}>{globalStats.books_today}</div>
                <div style={communityLabel}>Added today</div>
              </div>
            </div>

            <div style={communityStatItem}>
              <Clock size={16} color="#2196f3" />
              <div>
                <div style={communityVal}>{Math.round(globalStats.minutes_today / 60)}h</div>
                <div style={communityLabel}>Read today</div>
              </div>
            </div>

            <div style={communityStatItem}>
              <BookOpen size={16} color="#9c27b0" />
              <div>
                <div style={communityVal}>{globalStats.total_books.toLocaleString()}</div>
                <div style={communityLabel}>Total books</div>
              </div>
            </div>

            {globalStats.top_book && globalStats.top_book !== 'None yet' && (
            <div style={{ ...communityStatItem, gridColumn: 'span 3', borderTop: '1px solid var(--border)', paddingTop: '15px', marginTop: '5px' }}>
              <Trophy size={20} color="#ffc107" />
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#8d6e63' }}>TOP BOOK THIS MONTH</div>
                <a
                    href={getBookshopLink({ title: globalStats.top_book })}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--text-main)', fontWeight: 'bold', fontSize: '1rem', textDecoration: 'underline' }}
                    title="Find this book on Bookshop.org"
                  >
                    {globalStats.top_book}
                  </a>
              </div>
            </div>
            )}

            <div style={{ ...communityStatItem, gridColumn: 'span 3', borderTop: '1px solid var(--border)', paddingTop: '15px' }}>
              <Sparkles size={20} color="var(--accent)" />
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#8d6e63' }}>LEADING GENRE</div>
                <div style={{ color: 'var(--text-main)', fontWeight: 'bold', fontSize: '1rem' }}>{globalStats.top_genre}</div>
              </div>
            </div>

            {globalStats.top_reader?.count > 0 && (
            <div style={{ ...communityStatItem, gridColumn: 'span 3', borderTop: '1px solid var(--border)', paddingTop: '15px' }}>
              <Users size={20} color="#2196f3" />
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#8d6e63' }}>TOP READER THIS MONTH</div>
                <div style={{ color: 'var(--text-main)', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  <span onClick={() => globalStats.top_reader.id && setPeekUserId(globalStats.top_reader.id)} style={{ cursor: 'pointer', color: 'var(--accent)', fontWeight: 'bold', fontSize: '1.1rem', textDecoration: 'underline' }}>{globalStats.top_reader.name}</span> <span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: 'var(--text-soft)' }}>({globalStats.top_reader.count} books)</span>
                </div>
              </div>
            </div>
            )}
          </div>
        )}
      </div>

      <ShelfSection
        icon={<Flame size={20} color="#ff5722" />}
        title="Trending Now"
        subtext="What the community is picking up most, right now."
        books={trending}
      />
      <ShelfSection
        icon={<Award size={20} color="#ffc107" />}
        title="Award Winners"
        subtext="Hidden gems and standout new releases, picked from what members are reading."
        books={awards}
      />
      <ShelfSection
        icon={<BookOpen size={20} color="var(--accent)" />}
        title="NYT Bestsellers"
        subtext="Currently topping the New York Times bestseller lists. Purchases support indie bookstores & Leaf & Letter."
        books={bestsellers}
      />
      <ShelfSection
        icon={<LayoutGrid size={20} color="#9c27b0" />}
        title="Top by Genre"
        subtext="The most-finished book in each of the community's busiest genres."
        books={topGenres}
      />
      <ShelfSection
        icon={<ListOrdered size={20} color="#4caf50" />}
        title="Top of the Garden"
        subtext="The books that keep winning readers' personal ranking brackets."
        books={topGarden}
      />

      <SanctuaryFavorites />

      {readers.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h4 style={{ fontSize: '0.7rem', color: 'var(--text-soft)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Open Nooks</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {readers.map(r => (
              <button key={r.id} onClick={() => setPeekUserId(r.id)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '20px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-app)', cursor: 'pointer', color: 'var(--text-main)', fontSize: '0.85rem', fontWeight: 'bold' }}>
                <span style={{ fontSize: '1.2rem' }}>{r.avatar_url || '📚'}</span>
                {r.username || 'Reader'}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ ...sectionContainer, marginTop: '1.5rem' }}>
        <h4 style={{ fontSize: '0.7rem', color: 'var(--text-soft)', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Leaf &amp; Letter HQ</h4>
        <div style={hubGrid}>
          {HUB_LINKS.map(({ tab, label, Icon }) => (
            <button key={tab} onClick={() => setHubTab(tab)} style={hubButton}>
              <Icon size={16} color="var(--accent)" /> {label}
            </button>
          ))}
        </div>
      </div>

      {hubTab && <CommunityModal initialTab={hubTab} onClose={() => setHubTab(null)} />}
      {peekUserId && <UserProfileModal userId={peekUserId} onClose={() => setPeekUserId(null)} />}
    </div>
  );
};

// Styles
const sectionContainer = {
  backgroundColor: 'var(--bg-card)', padding: '1.5rem', borderRadius: '20px',
  border: '1px solid var(--border)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
  marginBottom: '1.5rem'
};
const sectionHeader = { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '0.75rem' };
const shelfSubtext = { fontSize: '0.8rem', color: 'var(--text-soft)', marginTop: 0, marginBottom: '14px' };
const loggedOutBannerStyle = {
  display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px',
  marginBottom: '1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)',
  borderRadius: '10px', fontSize: '0.85rem', color: 'var(--text-main)'
};
const featuredShelfWood = {
  display: 'flex', gap: '14px', overflowX: 'auto', padding: '14px 10px 18px 10px',
  backgroundColor: WOOD_THEMES[DEFAULT_WOOD_THEME_KEY].surface, borderRadius: '8px',
  borderBottom: `10px solid ${WOOD_THEMES[DEFAULT_WOOD_THEME_KEY].drawer}`
};
const featuredBookLink = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', textDecoration: 'none', flexShrink: 0, width: '90px' };
const featuredBookCover = { width: '80px', height: '120px', objectFit: 'cover', borderRadius: '4px', backgroundColor: WOOD_THEMES[DEFAULT_WOOD_THEME_KEY].drawer, boxShadow: '0 3px 8px rgba(0,0,0,0.3)' };
const featuredBookLabel = { fontSize: '0.68rem', color: WOOD_THEMES[DEFAULT_WOOD_THEME_KEY].knob, textAlign: 'center', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' };
const featuredBookSubLabel = { fontSize: '0.6rem', color: WOOD_THEMES[DEFAULT_WOOD_THEME_KEY].knob, opacity: 0.75, textAlign: 'center' };
const hubGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' };
const hubButton = {
  display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', borderRadius: '12px',
  border: '1px solid var(--border)', backgroundColor: 'var(--bg-app)', color: 'var(--text-main)',
  fontSize: '0.85rem', fontWeight: 'bold', cursor: 'pointer', textAlign: 'left'
};
const communityGrid = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' };
const communityStatItem = { display: 'flex', alignItems: 'flex-start', gap: '10px' };
const communityVal = { fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-main)', lineHeight: '1' };
const communityLabel = { fontSize: '0.65rem', color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' };

export default Community;
